import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { ApiError, apiGet, apiSend } from "../lib/api.js";
import type {
  NotificationListItem,
  NotificationListResponse,
  UnreadCountResponse,
} from "./types.js";

/**
 * GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 (Wave A) — TanStack Query
 * hooks for the Notification Center.
 *
 * Cache shape (Phase 0 §5.2 — all keys share the
 * `["me","notifications"]` prefix so ONE prefix invalidation reaches
 * every notification query; the prefix does not collide with
 * `["me","notification-preferences"]`):
 *
 *   ["me","notifications","unread-count"]        → badge scalar
 *   ["me","notifications","list",{unread}]       → infinite list
 *
 * Storm rules (binding — Phase 0 §5.3/§5.5, FIX-02..09 lineage):
 *   - the ONLY poll is the unread-count 60 s fallback interval
 *     (TanStack pauses it on hidden tabs by default via
 *     `refetchIntervalInBackground: false`);
 *   - the list paginates by explicit user click only — no
 *     auto-drain, no effect-driven `fetchNextPage`;
 *   - 429/4xx are never blind-retried (global QueryClient policy in
 *     `main.tsx` via `lib/rateLimitRetry.ts`).
 */

export const NOTIFICATIONS_QUERY_PREFIX = ["me", "notifications"] as const;

/**
 * The 60-second fallback poll (Phase 0 §6.4). Realtime
 * `notifications.hint` is the primary refresh signal; this interval
 * keeps the badge honest when the socket is down. MUST NOT go below
 * 60_000 (source-guarded).
 */
export const UNREAD_COUNT_REFETCH_INTERVAL_MS = 60_000;

export function useUnreadNotificationCount() {
  return useQuery<UnreadCountResponse, Error>({
    queryKey: [...NOTIFICATIONS_QUERY_PREFIX, "unread-count"],
    queryFn: ({ signal }) =>
      apiGet<UnreadCountResponse>("/v1/me/notifications/unread-count", undefined, signal),
    staleTime: 30_000,
    refetchInterval: UNREAD_COUNT_REFETCH_INTERVAL_MS,
  });
}

const LIST_PAGE_SIZE = 20;

export function useNotificationsList({ unreadOnly }: { unreadOnly: boolean }) {
  return useInfiniteQuery<NotificationListResponse, Error>({
    queryKey: [...NOTIFICATIONS_QUERY_PREFIX, "list", { unread: unreadOnly }],
    queryFn: ({ pageParam, signal }) =>
      apiGet<NotificationListResponse>(
        "/v1/me/notifications",
        {
          limit: LIST_PAGE_SIZE,
          cursor: typeof pageParam === "string" ? pageParam : undefined,
          // The backend treats an absent `unread` as "all rows"; only
          // send the flag when filtering (querystring booleans coerce).
          unread: unreadOnly ? "true" : undefined,
        },
        signal,
      ),
    initialPageParam: undefined,
    // null → undefined: TanStack reads `undefined` as "no next page".
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

/** Flatten the infinite pages into one row list for rendering. */
export function flattenNotificationPages(
  pages: readonly NotificationListResponse[] | undefined,
): NotificationListItem[] {
  return pages?.flatMap((p) => p.items) ?? [];
}

function invalidateNotifications(qc: QueryClient): void {
  // Prefix invalidation (exact:false) — refreshes the unread-count
  // badge everywhere and the list when the Center page is mounted
  // (TanStack default refetchType "active" only refetches mounted
  // queries).
  void qc.invalidateQueries({ queryKey: [...NOTIFICATIONS_QUERY_PREFIX], exact: false });
}

/**
 * PATCH read/unread state. A 404 means the recipient row vanished
 * (pruned, or never the caller's) — Phase 0 §5.4 pins that as a
 * SILENT refetch, not a blocking error: we swallow the 404 and let
 * the follow-up invalidation drop the stale row from the list.
 * Optimistic updates are deliberately avoided in v1 (same section).
 */
async function patchReadState(id: string, read: boolean): Promise<void> {
  try {
    await apiSend<{ ok: true }>("PATCH", `/v1/me/notifications/${id}/${read ? "read" : "unread"}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return;
    throw err;
  }
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => patchReadState(id, true),
    onSettled: () => invalidateNotifications(qc),
  });
}

export function useMarkNotificationUnread() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => patchReadState(id, false),
    onSettled: () => invalidateNotifications(qc),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<{ marked: number }, Error, void>({
    mutationFn: () => apiSend<{ marked: number }>("POST", "/v1/me/notifications/mark-all-read"),
    onSettled: () => invalidateNotifications(qc),
  });
}
